import { ReactNode, useState } from 'react';
import { formatDate, FormatReferralStatus } from 'utils/format';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReferralDto } from 'common/api';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Services } from 'services';
import { Store } from 'store';
import {
  Box,
  Button,
  RadioButton,
  Spinner,
  Text,
} from '@clariness/clarikit-react';
import { EyeOnIcon } from '@clariness/clarikit-icons';
import {
  getMrrDisplay,
  getMrrStatusLabel,
  hasRecordedSeqsterMrConsent,
  seqsterConsentWasStored,
} from './mrr-display';
import { openMedicalRecords } from './open-medical-records';
type PatientDetailsBasicProps = {
  referral: ReferralDto;
};

export const PatientDetailsBasic = ({ referral }: PatientDetailsBasicProps) => {
  const intl = useIntl();
  const siterxOptinEnabled = useFeatureFlagEnabled('SITERX_OPTIN_ENABLED');
  const seqsterOptinEnabled = useFeatureFlagEnabled('SEQSTER_OPTIN_ENABLED');
  const { openModal } = Store.useModal();
  const [consentRecordedThisSession, setConsentRecordedThisSession] =
    useState(false);
  const { requestMedicalRecords, isLoading: isRequestingMedicalRecords } =
    Services.Referrals.useRequestMedicalRecords({
      onError: (error) => {
        if (seqsterConsentWasStored(error.statusCode)) {
          setConsentRecordedThisSession(true);
        }
      },
    });
  const { getViewingLink, isLoading: isViewingMedicalRecords } =
    Services.Referrals.useMedicalRecordsViewingLink();

  const { update601VisitStatus } = Services.Referrals.useUpdate601VisitStatus(
    {}
  );

  const isEnUs = referral?.language?.code === 'en-US';
  const {
    showSiterxMrr,
    showSeqsterMrr,
    showSeqsterRequestLink,
    showSeqsterViewLink,
  } = getMrrDisplay({
    isEnUs,
    siterxFlagEnabled: Boolean(siterxOptinEnabled),
    seqsterFlagEnabled: Boolean(seqsterOptinEnabled),
    siterxOptin: referral.siterxOptin,
    seqsterOptin: referral.seqsterOptin,
    studySiteStatus: referral.studySiteStatus,
    medicalRecordsRequestStatus: referral.medicalRecordsRequestStatus,
    isRequesting: isRequestingMedicalRecords,
    isViewing: isViewingMedicalRecords,
  });
  const mrrStatusLabel = referral.medicalRecordsRequestStatus
    ? getMrrStatusLabel(referral.medicalRecordsRequestStatus)
    : undefined;
  const consentAlreadyRecorded =
    hasRecordedSeqsterMrConsent(referral.seqsterMrConsentReceived) ||
    consentRecordedThisSession;

  const submitMedicalRecordsRequest = () => {
    if (!referral.id) {
      return;
    }
    requestMedicalRecords({
      referralId: referral.id,
      consentConfirmed: true,
    });
  };

  const handle601VisitCompletedStatus = (status: string) => {
    if (referral?.id) {
      update601VisitStatus({
        referralId: referral?.id,
        visit601Status: status,
      });
    }
  };

  const rows: {
    id: string;
    label: string;
    value: ReactNode;
    hidden?: boolean;
  }[] = [
    {
      id: 'patient-id',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.patient_id',
        defaultMessage: 'Patient ID',
      }),
      value: referral?.patient.internalId,
    },
    {
      id: 'first-name',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.first_name',
        defaultMessage: 'First Name',
      }),
      value: referral?.patient.firstName,
    },
    {
      id: 'last-name',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.last_name',
        defaultMessage: 'Last Name',
      }),
      value: referral?.patient.lastName,
    },
    {
      id: 'gender',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.gender',
        defaultMessage: 'Gender',
      }),
      value: (
        <>
          {referral?.gender === ReferralDto.gender.M
            ? intl.formatMessage({
                id: 'is.patient_details_modal.basic_details.gender.male',
                defaultMessage: 'Male',
              })
            : ''}
          {referral?.gender === ReferralDto.gender.F
            ? intl.formatMessage({
                id: 'is.patient_details_modal.basic_details.gender.female',
                defaultMessage: 'Female',
              })
            : ''}
          {referral?.gender === ReferralDto.gender.U
            ? intl.formatMessage({
                id: 'is.patient_details_modal.basic_details.gender.other',
                defaultMessage: 'Other',
              })
            : ''}
        </>
      ),
    },
    {
      id: 'patient-status',
      label: intl.formatMessage({
        id: 'is.patient_status.title',
        defaultMessage: 'Patient Status',
      }),
      value:
        referral?.status &&
        FormatReferralStatus(referral.status, referral.appointmentDateTime),
    },
    {
      id: '601-visit-status',
      hidden: !referral.study?.has601Visit,
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.601_visit_status',
        defaultMessage: '601 visit completed',
      }),
      value: (
        <Box display="flex" alignItems="center" gap="16">
          {(['yes', 'no'] as const).map((status) => {
            const isSelected = referral.isVisit601Completed === status;
            return (
              <RadioButton
                key={status}
                id={`601_visit_status_${status}`}
                labelText={
                  status === 'yes'
                    ? intl.formatMessage({
                        id: 'is.patient_details_modal.basic_details.601_visit_status.yes',
                        defaultMessage: 'Yes',
                      })
                    : intl.formatMessage({
                        id: 'is.patient_details_modal.basic_details.601_visit_status.no',
                        defaultMessage: 'No',
                      })
                }
                selected={isSelected}
                // TODO(clarikit): RadioButton toggles itself off when clicked while selected.
                onClickCapture={(event) =>
                  isSelected && event.stopPropagation()
                }
                handleSelect={(select) =>
                  select && handle601VisitCompletedStatus(status)
                }
              />
            );
          })}
        </Box>
      ),
    },
    {
      id: 'age',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.age',
        defaultMessage: 'Age',
      }),
      value: referral?.ageAtTimeOfRegister ? (
        <FormattedMessage
          id="is.patient_details_modal.basic_details.age_clarification"
          defaultMessage="{ age } (at point of registration)"
          values={{ age: referral?.ageAtTimeOfRegister }}
        />
      ) : null,
    },
    {
      id: 'registration-date',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.registration_date',
        defaultMessage: 'Registered on',
      }),
      value: formatDate(
        intl.formatDate(
          referral &&
            new Date(
              referral.registrationDate
                ? referral.registrationDate
                : referral.referralDate
            )
        )
      ),
    },
    {
      id: 'mrr-status',
      hidden: !(showSiterxMrr || showSeqsterMrr),
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.MMR_status',
        defaultMessage: 'MRR status',
      }),
      value: showSeqsterMrr ? (
        <Box display="flex" alignItems="center" gap="8">
          {mrrStatusLabel && (
            <Box display="flex" flexDirection="column" gap="2px">
              <Text as="span" variant="bodyMd">
                <FormattedMessage {...mrrStatusLabel.label} />
              </Text>
              {mrrStatusLabel.note && (
                <Text as="p" variant="bodySm" color="color-gray-variant-2">
                  <FormattedMessage {...mrrStatusLabel.note} />
                </Text>
              )}
            </Box>
          )}
          {showSeqsterRequestLink && (
            <Button
              variant="tertiary"
              size="m"
              onClick={() => {
                if (consentAlreadyRecorded) {
                  submitMedicalRecordsRequest();
                  return;
                }
                openModal({
                  modalType: 'RequestMedicalRecordsConsent',
                  modalProps: {
                    onConfirm: submitMedicalRecordsRequest,
                  },
                });
              }}
            >
              {intl.formatMessage({
                id: 'is.request_medical_records.link',
                defaultMessage: 'Request medical records',
              })}
            </Button>
          )}
          {showSeqsterViewLink && (
            <Button
              variant="tertiary"
              size="s"
              colorVariant="green"
              iconLeft={EyeOnIcon}
              onClick={() => {
                if (referral.id) {
                  openMedicalRecords(referral.id, getViewingLink);
                }
              }}
            >
              {intl.formatMessage({
                id: 'is.view_medical_records.link',
                defaultMessage: 'See records',
              })}
            </Button>
          )}
          {(isRequestingMedicalRecords || isViewingMedicalRecords) && (
            <Spinner size="small" />
          )}
        </Box>
      ) : (
        showSiterxMrr && referral.siterxStatus
      ),
    },
    {
      id: 'language',
      label: intl.formatMessage({
        id: 'is.patient_details_modal.basic_details.language',
        defaultMessage: 'Language',
      }),
      value: referral?.language.name,
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap="8" width="100%">
      <Text as="h6" variant="headingXs">
        {intl.formatMessage({
          id: 'is.patient_details_modal.basic_details_title',
          defaultMessage: 'Basic Details',
        })}
      </Text>
      <Box width="100%" height="1px" backgroundColor="color-gray-variant-4" />
      <Box as="dl" display="flex" flexDirection="column" gap="16" margin="0">
        {rows
          .filter((row) => !row.hidden)
          .map((row) => (
            <Box
              key={row.id}
              display="flex"
              flexWrap="wrap"
              gap="40"
              width="100%"
            >
              <Box as="dt" style={{ flex: '0 0 150px' }}>
                <Text as="span" variant="bodyMd" fontWeight="bold">
                  {row.label}
                </Text>
              </Box>
              <Box
                as="dd"
                margin="0"
                minWidth="0"
                style={{ flex: '1 1 12rem' }}
              >
                <Text as="span" variant="bodyMd">
                  {row.value}
                </Text>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
};
